import type { RefObject } from 'react';

/** Compose Radix autofocus handlers without overriding a caller's prevented event. */
type FocusTarget = RefObject<HTMLElement | null> | undefined;
const availableTarget = (targets: FocusTarget[]) => targets.map((ref) => ref?.current).find((element) => element?.isConnected && !element.matches(':disabled') && !element.closest('[inert]'));

export function focusConnected(event: Event, ...targets: FocusTarget[]): boolean {
  if (event.defaultPrevented) return false;
  const target = availableTarget(targets);
  if (target) {
    event.preventDefault();
    target.focus();
    return true;
  }
  return false;
}

export function restoreFocus(event: Event, ...targets: FocusTarget[]): void {
  if (event.defaultPrevented || focusConnected(event, ...targets)) return;
  // List refresh can temporarily disable the opener while its dialog unmounts. Restore once ready,
  // unless the user has moved focus or started another interaction. Removed openers need an explicit fallback.
  if (!targets.some((ref) => ref?.current?.isConnected)) return;
  event.preventDefault();
  const cancel = () => {
    observer.disconnect(); clearTimeout(timer);
    document.removeEventListener('pointerdown', cancel, true);
    document.removeEventListener('keydown', cancel, true);
    document.removeEventListener('focusin', focusMoved, true);
  };
  const focusMoved = () => { if (document.activeElement !== document.body) cancel(); };
  const observer = new MutationObserver(() => {
    const ready = availableTarget(targets);
    if (ready) { cancel(); ready.focus(); }
  });
  observer.observe(document.body, {subtree:true,childList:true,attributes:true,attributeFilter:['disabled','inert']});
  document.addEventListener('pointerdown', cancel, true);
  document.addEventListener('keydown', cancel, true);
  document.addEventListener('focusin', focusMoved, true);
  const timer = setTimeout(cancel, 3000);
}
